import * as Api from '@ViewModels';
import { ESelectionState } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	IQuickAddEntityComponentProps,
	IUserSessionComponentProps,
	QuickAddEntityViewModelKey,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { useInput } from '../../../../models/hooks/inputHooks';
import { useCompaniesQuery } from '../../../../queries/Company/useCompaniesQuery';
import { Button } from '../../../components-v2/Button';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Column, Row } from '../../../components-v2/PageLayout';
import { ROUTES } from '../../../constants/routes';
import { bs } from '../../../styles/styles';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { ContactsTableLoader } from '../shared/ContactsTableLoader';
import { CompaniesActionsBar } from './CompaniesActionsBar';
import { CompaniesSearchInputContext, CompanySearchContext, DefaultCompaniesSort } from './CompaniesContext';
import { CompaniesGlobalSearch } from './CompaniesGlobalSearch';
import { CompaniesSearchBarTypeTabs } from './CompaniesSearchBarTypeTabs';
import { CompaniesSearchDescription } from './CompaniesSearchDescription';
import { CompaniesTable } from './CompaniesTable';
import { styleSheet } from './styles';

export function useCompaniesPageUrlState() {
	return useQueryStates({
		companySearchProperty: parseAsInteger.withDefault(Api.CompanySearchRequestProperty.Name),
		companySearchValue: parseAsString.withDefault(''),
	});
}

export const CompaniesContainer: React.FC<
	IQuickAddEntityComponentProps & IFullscreenModalComponentProps & IUserSessionComponentProps
> = inject(
	QuickAddEntityViewModelKey,
	FullScreenModalViewModelKey,
	UserSessionViewModelKey
)(
	observer(props => {
		const { quickAddEntity } = props;
		const history = useHistory();
		const [{ companySearchProperty, companySearchValue }] = useCompaniesPageUrlState();
		const querySort = DefaultCompaniesSort; // followup use nuqs for sort too

		const [selectionState, setSelectionState] = React.useState<ESelectionState>(ESelectionState.None);
		const [selectedCompanyIds, setSelectedCompanyIds] = React.useState<string[]>([]);
		const [excludedCompanyIds, setExcludedCompanyIds] = React.useState<string[]>([]);

		const companySearch = React.useMemo(
			() => ({ property: companySearchProperty, value: companySearchValue }) as Api.Search,
			[companySearchProperty, companySearchValue]
		);

		const [sort, setSort] = React.useState<Api.ISortDescriptor>(querySort);
		const companiesQuery = useCompaniesQuery({
			searchRequest: { searches: [companySearch] },
			sortDescriptor: sort,
		});

		const companies = React.useMemo(
			() => (companiesQuery.data?.pages?.flatMap(page => page.values) ?? []) as Api.ICompany[],
			[companiesQuery.data]
		);

		const filterValue = React.useMemo(
			() => ({
				companies,
				sort,
				setSort,
				selectionState,
				setSelectionState,
				selectedCompanyIds,
				setSelectedCompanyIds,
				excludedCompanyIds,
				setExcludedCompanyIds,
				companiesQuery,
			}),
			[
				sort,
				setSort,
				companies,
				selectionState,
				setSelectionState,
				selectedCompanyIds,
				setSelectedCompanyIds,
				excludedCompanyIds,
				setExcludedCompanyIds,
				companiesQuery,
			]
		);

		// Search Input Context

		const [search, setSearch, onSearchChange] = useInput(companySearch.value ?? '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
		const searchValue = React.useMemo(() => ({ onSearchChange, search, setSearch }), [search]);

		React.useEffect(() => {
			setSearch(companySearchValue);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [companySearchValue]);

		// "New Company" modal
		const onShowQuickAddContactModal = React.useCallback(() => {
			quickAddEntity.show({
				entity: {},
				onComplete: (error: Api.IOperationResultNoValue, companyModel?: Api.ICompany) => {
					if (!error && !!companyModel) {
						quickAddEntity.reset();
						history.push({
							pathname: `${ROUTES.contacts.companies}/${companyModel.id}`,
						});
					}
				},
				type: 'company',
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return (
			<>
				<CompaniesSearchInputContext.Provider value={searchValue}>
					<CompanySearchContext.Provider value={filterValue}>
						<Column styles={[bs.gap4, styleSheet.container]}>
							<Row styles={[styleSheet.searchRow]}>
								<Row styles={[styleSheet.searchControls]}>
									<CompaniesGlobalSearch />
									<CompaniesSearchBarTypeTabs />
								</Row>
								<Button onClick={onShowQuickAddContactModal}>New Company</Button>
							</Row>
							<div className={css(styleSheet.searchDescription)}>
								<CompaniesSearchDescription />
							</div>
							<CompaniesActionsBar />
							{companiesQuery.isPending && !companies.length ? <ContactsTableLoader /> : <CompaniesTable />}
						</Column>
					</CompanySearchContext.Provider>
				</CompaniesSearchInputContext.Provider>
				<AppDrawer>
					<StickyAppDrawerContent value='ai-assistant'>
						<DefaultUniversalAssistantChat />
					</StickyAppDrawerContent>
					<AppDrawerToolbar>
						<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
					</AppDrawerToolbar>
				</AppDrawer>
			</>
		);
	})
);
